import { createContext, useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY = "lt_legal_consent";
const ConsentContext = createContext(null);
const HIDDEN_PATHS = ["/privacy", "/terms", "/driver-consent", "/login"];

export function ConsentProvider({ children }) {
  const [status, setStatus] = useState(() => localStorage.getItem(KEY));
  const [open, setOpen] = useState(() => !localStorage.getItem(KEY));
  const { pathname } = useLocation();

  const decide = (value) => {
    localStorage.setItem(KEY, value);
    localStorage.setItem(`${KEY}_at`, new Date().toISOString());
    setStatus(value);
    setOpen(false);
  };

  const hidden = pathname.startsWith("/admin") || HIDDEN_PATHS.includes(pathname);

  return (
    <ConsentContext.Provider value={{ accepted: status === "accepted", reopen: () => setOpen(true) }}>
      {children}
      {open && !hidden && <ConsentModal onAccept={() => decide("accepted")} onDecline={() => decide("declined")} />}
    </ConsentContext.Provider>
  );
}

function ConsentModal({ onAccept, onDecline }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="consent-modal" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <div className="w-full max-w-lg rounded-2xl border border-[var(--lt-border)] bg-[var(--lt-panel)] p-6 sm:p-8 shadow-2xl page-enter">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="h-6 w-6 text-[var(--lt-accent)]" />
          <h2 id="consent-title" className="font-head font-bold uppercase tracking-widest text-base md:text-lg">Your Privacy Matters</h2>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Lucas Trans Inc. uses your information only to respond to quotes, messages and driver applications. Please review and accept our{" "}
          <Link to="/privacy" className="text-[var(--lt-accent)] underline" data-testid="consent-privacy-link">Privacy Policy</Link> and{" "}
          <Link to="/terms" className="text-[var(--lt-accent)] underline" data-testid="consent-terms-link">Terms &amp; Conditions</Link> to continue.
        </p>
        <p className="text-xs text-slate-500 mt-3">If you decline, you can still browse the site, but our forms will be disabled.</p>
        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button variant="outline" onClick={onDecline} data-testid="consent-decline-btn" className="bg-transparent border-[var(--lt-border)] rounded-xl h-11 px-6 font-head uppercase tracking-widest text-xs">Decline</Button>
          <Button onClick={onAccept} data-testid="consent-accept-btn" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-11 px-8 font-head uppercase tracking-widest text-xs">Accept</Button>
        </div>
      </div>
    </div>
  );
}

export const useConsent = () => useContext(ConsentContext);
